import { Button } from "../../components/button.component";
import { Form } from "rsuite";
import { useHistory } from "react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  TextInput,
  SelectPickerInput,
} from "../../components/textInput.component";
import { UserFormSchema } from "../../models/forms/userForm";
import { ApiSchemaUser } from "../../models/api/users";
import { ApiCompany } from "../../core/apis";
import { DoesObjectHasAnyInvalidValue } from "../../utils/formUtils";
import { AppRouteUi } from "../../config/appRoute";
import { SpinLoader } from "../../components/spinLoader.component";
import { ApiSchemaCompany } from "../../models/api";

type UsersFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  department: string;
  companySKey: string;
  userName: string;
};

export const UserForm = ({
  isLoading,
  isValidating,
  handleSubmit,
  value,
}: {
  isLoading: boolean;
  isValidating?: boolean;
  handleSubmit: (formData: any) => void;
  value?: ApiSchemaUser;
}) => {
  const routeHistory = useHistory();
  const [formError, setFormError] = useState({});
  const [formData, setFormData] = useState<UsersFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    department: "",
    companySKey: "",
    userName: "",
  });

  const [companyData, setCompanyData] = useState<ApiSchemaCompany[]>([]);

  useEffect(
    () =>{
      value &&
      setFormData({
        firstName: value.firstName,
        lastName: value.lastName,
        email: value.email,
        phoneNumber: value.phoneNumber,
        department: value.department,
        userName: value.userName || "",
        companySKey: value.companySKey || "",
      });
      convertToPhoneNumberFormat(value?.phoneNumber ? value?.phoneNumber : "", "phoneNumber");
    },
    [value]
  );

  const [isDisabled, setIsDisabled] = useState(true);

  const departmentsSelectItems = ["FinanceManager", "HrOfficer", "Admin"].map(
    (dept) => ({
      label: dept.replace(/([a-z0-9])([A-Z])/g, "$1 $2"),
      value: dept,
    })
  );

  useEffect(() => {
    ApiCompany.GetAll().then((res) => {
      setCompanyData(res.data!);
    });
  }, []);
  const companySelectItems = useMemo(
    () =>
      companyData
        ?.filter((company) =>
          formData.department === "Admin"
            ? !company.isAuthority
            : company.isAuthority
        )
        .map((company) => ({
          label: company.displayName ?? company.name,
          value: company.sKey,
        })),
    [companyData, formData.department]
  );

  useEffect(() => {
    const isEmpty = Object.values({ ...formData }).some(
      DoesObjectHasAnyInvalidValue
    );
    const errorCount = Object.keys(formError).length;
    var isDisabled = isEmpty || !!errorCount;
    setIsDisabled(isDisabled);
  }, [formData, formError]);

  const convertToPhoneNumberFormat = (phoneNumber: string, propertyName: string) => {
    phoneNumber = phoneNumber.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
  
    if (phoneNumberLength > 3 && phoneNumberLength < 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumberLength >= 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  
    setFormData((prev) => ({ ...prev, [propertyName]: phoneNumber }));
  }

  const handleChange = useCallback(
    (data: any, _: any) =>
      setFormData((prevFormData) => ({ ...prevFormData, ...data })),
    []
  );

  if (isValidating) return <SpinLoader />;

  return (
    <Form
      className="row"
      onChange={handleChange}
      onSubmit={() => handleSubmit(formData)}
      onCheck={setFormError}
      model={UserFormSchema}
      formValue={formData}
      fluid
    >
      <div className="col-md-10 col-12 border-end px-4 add-page-ctl">
        <div className="row">
          <TextInput name="firstName" required>
            First Name
          </TextInput>
          <TextInput name="lastName" required>
            Last Name
          </TextInput>
        </div>

        <div className="row">
          <TextInput name="userName" disabled={!!value} required>
            Username
          </TextInput>
          <SelectPickerInput
            data={departmentsSelectItems}
            name="department"
            onChange={(e) => {
              setIsDisabled(true);
              setFormData({ ...formData, department: e, companySKey: "" });
            }}
            required
            disabled={!!value}
            renderEmpty={<p className="text-muted p-3">No Data Available</p>}
          >
            Department
          </SelectPickerInput>
        </div>

        {!!formData.department ? (
          <>
            <div className="row">
              <TextInput 
              name="phoneNumber" 
              required
              value={formData.phoneNumber}
              onChange={(val) => convertToPhoneNumberFormat(val, "phoneNumber")}
              >
                Phone Number
              </TextInput>
              <TextInput name="email" disabled={!!value} required>
                Email
              </TextInput>
            </div>

            <div className="row">
              <SelectPickerInput
                data={companySelectItems}
                name="companySKey"
                required
                disabled={!!value}
                renderEmpty={
                  <p className="text-muted p-3">No Data Available</p>
                }
              >
                Company
              </SelectPickerInput>
              {!!!value && formData.email && (
                <div className="col-md-6 col-12 my-auto px-1">
                  <div className="alert alert-info my-auto" role="alert">
                    <span>
                      <strong>Note:</strong> After Creating the user an email
                      will be sent to "{formData.email}". Please click the link
                      to activate your account.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>

      <div className="col-md-2 col-12">
        <Button
          className="m-1 w-100 btn-green"
          type="submit"
          name="Save"
          loading={isLoading}
          disabled={isDisabled}
        />
        <Button
          onClick={() => {
            routeHistory.length > 1
              ? routeHistory.goBack()
              : routeHistory.push(AppRouteUi.Root);
          }}
          type="button"
          className="m-1 w-100 btn-green-subtle"
          disabled={isLoading}
          name="Return without saving"
        />
      </div>
    </Form>
  );
};
